/* eslint-disable array-bracket-newline */
/**
 * Hook to update wallet state
 */
import React, { useCallback, useEffect } from 'react';
import { Network } from '@kattana-io/common';
import { ELROND_CHAIN_ID } from '../../helpers/getLibrary';
import { KattanaAPI, subscription } from '../../store/createStore';
import { OnRefresh } from '../walletContext';
import { WalletActions, IWalletState } from '../walletReducer';
import { BASE_TOKEN } from '../../components/Trading/helpers/nativeCoins';

const DELAY = 5000;
export const NATIVE_LINK = 'native';

interface IUseLoadWalletData {
  account?: string;
  chainId?: any;
  noprovider: boolean;
  dispatch: React.Dispatch<{ payload?: any; type: WalletActions }>;
  state: IWalletState;
}

const useLoadWalletData = ({
  account,
  chainId,
  noprovider,
  dispatch,
  state,
}: IUseLoadWalletData) => {
  const network = Network.fromChainId(chainId);
  const networkSym = network?.getSymbol();

  /**
   * Fetch info(rates, balances, etc.) for current wallet
   */
  const fetchWalletData = useCallback(async () => {
    if (chainId && account) {
      try {
        dispatch({
          type: WalletActions.LoadWallet,
          payload: { chain: chainId, account },
        });
        const chain = chainId == ELROND_CHAIN_ID ? 'multiversx' : chainId;
        const isV2 = network?.isOKX();

        // Lets load balance
        const data = await KattanaAPI.balances(chain, account, isV2);
        if (data) {
          dispatch({ type: WalletActions.SetWallet, payload: data });
        } else {
          dispatch({ type: WalletActions.ToggleWalletError });
        }
        subscription.subscribeToPriceAlerts(account);
        subscription.subscribeToLimitOrders(account);
      } catch (err) {
        console.warn(err);
        dispatch({ type: WalletActions.ResetWallet });
      }
    }
  }, [account, chainId]);

  /**
   * Refetch balances for specific wallet tokens and update wallet state
   */
  const onRefresh = useCallback<OnRefresh>(
    (addresses) => {
      if (networkSym && account) {
        window.setTimeout(async () => {
          dispatch({
            type: WalletActions.LoadWallet,
            payload: { chain: chainId, account },
          });
          try {
            const queries = addresses.filter(
              (address) => address !== BASE_TOKEN && address
            );
            queries.push(NATIVE_LINK);

            const data = await Promise.allSettled(
              queries.map((query) =>
                KattanaAPI.tokenBalanceForWallet(networkSym, query, account)
              )
            );
            const balances: Record<string, string> = {};
            data.forEach((settledItem, index) => {
              if (settledItem.status === 'fulfilled' && settledItem.value) {
                balances[queries[index]] = settledItem.value;
              }
            });
            dispatch({
              type: WalletActions.JoinWalletBalances,
              payload: balances,
            });
          } catch (error) {
            dispatch({
              type: WalletActions.JoinWalletBalances,
              payload: {},
            });
            console.warn(error);
          }
        }, DELAY);
      }
    },
    [account, chainId, dispatch, networkSym]
  );

  /**
   * Triggers on account, noprovider and chain changes
   */
  useEffect(() => {
    if (chainId && account) {
      if (state.chain !== chainId || state.account !== account) {
        fetchWalletData();
      }
    } else {
      if (!noprovider) {
        dispatch({ type: WalletActions.ResetWallet });
      }
    }
  }, [fetchWalletData, noprovider]);

  return { onRefresh };
};

export default useLoadWalletData;
